import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import {
  Plus,
  Pencil,
  Trash2,
  Eye,
  EyeOff,
  LogOut,
  X,
  Upload,
  Loader2,
  Globe,
  KeyRound,
  Image as ImageIcon,
} from "lucide-react";
import { api, imgSrc, clearTokens, formatApiError } from "../lib/api";

const EMPTY_FORM = {
  slug: "",
  nome: "",
  categoria: "",
  descricao: "",
  descricaoCompleta: "",
  imagem: "",
  galeria: [],
  tecnologias: "",
  funcionalidades: "",
  objetivo: "",
  url: "#",
  botao: "VER PROJETO",
  ativo: true,
  ordem: 0,
};

const inputClass =
  "w-full rounded-xl border border-white/10 bg-white/[0.03] px-4 py-3 text-sm text-white placeholder-white/30 outline-none transition-colors duration-300 focus:border-[#00E5FF]/60";
const labelClass = "mb-2 block text-xs font-semibold tracking-[0.2em] text-white/50";

const ImagePicker = ({ label, value, onChange, testid }) => {
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/admin/upload", fd);
      onChange(data.url);
      toast.success("Imagem enviada.");
    } catch (err) {
      toast.error(formatApiError(err, "Falha ao enviar a imagem."));
    } finally {
      setUploading(false);
    }
  };

  return (
    <div>
      <span className={labelClass}>{label}</span>
      <div className="flex items-center gap-3">
        <div className="flex h-16 w-24 shrink-0 items-center justify-center overflow-hidden rounded-lg border border-white/10 bg-white/[0.03]">
          {value ? (
            <img src={imgSrc(value)} alt="Preview" className="h-full w-full object-cover object-top" />
          ) : (
            <ImageIcon className="h-5 w-5 text-white/25" />
          )}
        </div>
        <div className="flex min-w-0 flex-1 flex-col gap-2">
          <input
            type="text"
            value={value}
            onChange={(e) => onChange(e.target.value)}
            placeholder="/projects/exemplo.webp ou URL"
            data-testid={`${testid}-input`}
            className={inputClass}
          />
          <button
            type="button"
            onClick={() => inputRef.current?.click()}
            disabled={uploading}
            data-testid={`${testid}-upload-button`}
            className="inline-flex w-fit items-center gap-2 rounded-full border border-white/15 bg-white/5 px-4 py-2 text-xs font-semibold text-white/80 transition-colors duration-300 hover:bg-white/10 disabled:opacity-60"
          >
            {uploading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Upload className="h-3.5 w-3.5" />}
            {uploading ? "Enviando..." : "Enviar imagem"}
          </button>
        </div>
        <input ref={inputRef} type="file" accept="image/png,image/jpeg,image/webp" className="hidden" onChange={handleFile} />
      </div>
    </div>
  );
};

const Admin = () => {
  const navigate = useNavigate();
  const [projects, setProjects] = useState(null);
  const [form, setForm] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const galleryInputRef = useRef(null);
  const [galleryUploading, setGalleryUploading] = useState(false);
  const [pwOpen, setPwOpen] = useState(false);
  const [pwForm, setPwForm] = useState({ current: "", next: "", confirm: "" });
  const [pwSaving, setPwSaving] = useState(false);
  const [pwError, setPwError] = useState("");

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/projects");
      setProjects(data.projects);
    } catch (err) {
      toast.error(formatApiError(err, "Não foi possível carregar os projetos."));
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const logout = async () => {
    try {
      await api.post("/auth/logout");
    } catch (e) {
      /* ignora */
    }
    clearTokens();
    navigate("/admin/login");
  };

  const openNew = () => {
    setForm(EMPTY_FORM);
    setEditingId("new");
  };

  const openEdit = (p) => {
    setForm({
      ...p,
      tecnologias: (p.tecnologias || []).join(", "),
      funcionalidades: (p.funcionalidades || []).join("\n"),
    });
    setEditingId(p.id);
  };

  const set = (key) => (e) => {
    const value = e?.target ? (e.target.type === "checkbox" ? e.target.checked : e.target.value) : e;
    setForm((f) => ({ ...f, [key]: value }));
  };

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    const payload = {
      ...form,
      tecnologias: form.tecnologias.split(",").map((t) => t.trim()).filter(Boolean),
      funcionalidades: form.funcionalidades.split("\n").map((t) => t.trim()).filter(Boolean),
      ordem: Number(form.ordem) || 0,
      url: form.url.trim() || "#",
    };
    try {
      if (editingId === "new") {
        await api.post("/admin/projects", payload);
        toast.success("Projeto criado com sucesso.");
      } else {
        await api.put(`/admin/projects/${editingId}`, payload);
        toast.success("Projeto atualizado.");
      }
      setEditingId(null);
      setForm(null);
      await load();
    } catch (err) {
      toast.error(formatApiError(err, "Não foi possível salvar."));
    } finally {
      setSaving(false);
    }
  };

  const toggleAtivo = async (p) => {
    try {
      await api.put(`/admin/projects/${p.id}`, { ...p, ativo: !p.ativo });
      setProjects((list) => list.map((x) => (x.id === p.id ? { ...x, ativo: !p.ativo } : x)));
      toast.success(p.ativo ? "Projeto desativado (oculto do site)." : "Projeto ativado (visível no site).");
    } catch (err) {
      toast.error(formatApiError(err, "Não foi possível alterar o status."));
    }
  };

  const remove = async (p) => {
    if (confirmDelete !== p.id) {
      setConfirmDelete(p.id);
      setTimeout(() => setConfirmDelete(null), 4000);
      return;
    }
    try {
      await api.delete(`/admin/projects/${p.id}`);
      toast.success("Projeto excluído.");
      setConfirmDelete(null);
      await load();
    } catch (err) {
      toast.error(formatApiError(err, "Não foi possível excluir."));
    }
  };

  const addGalleryImage = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setGalleryUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/admin/upload", fd);
      setForm((f) => ({ ...f, galeria: [...f.galeria, data.url] }));
      toast.success("Imagem adicionada à galeria.");
    } catch (err) {
      toast.error(formatApiError(err, "Falha ao enviar a imagem."));
    } finally {
      setGalleryUploading(false);
    }
  };

  const openPasswordModal = () => {
    setPwForm({ current: "", next: "", confirm: "" });
    setPwError("");
    setPwOpen(true);
  };

  const changePassword = async (e) => {
    e.preventDefault();
    setPwError("");
    if (pwForm.next.length < 8) {
      setPwError("A nova senha precisa ter pelo menos 8 caracteres.");
      return;
    }
    if (pwForm.next !== pwForm.confirm) {
      setPwError("A confirmação não confere com a nova senha.");
      return;
    }
    setPwSaving(true);
    try {
      await api.post("/auth/change-password", {
        current_password: pwForm.current,
        new_password: pwForm.next,
      });
      toast.success("Senha alterada com sucesso.");
      setPwOpen(false);
    } catch (err) {
      setPwError(formatApiError(err, "Não foi possível alterar a senha."));
    } finally {
      setPwSaving(false);
    }
  };

  return (
    <div className="min-h-screen" data-testid="admin-panel">
      <div className="sticky top-0 z-40 border-b border-white/10 bg-[#030305]/85 backdrop-blur-xl">
        <div className="mx-auto flex h-[68px] max-w-6xl items-center justify-between px-6">
          <div className="flex items-center gap-3">
            <span className="relative block h-9 w-9 rounded-lg bg-gradient-to-br from-[#00E5FF] to-[#8A2BE2] p-[1.5px]">
              <span className="flex h-full w-full items-center justify-center rounded-[7px] bg-[#050508]">
                <span className="text-gradient font-display text-lg font-black">E</span>
              </span>
            </span>
            <span className="font-display text-sm font-bold tracking-[0.18em]">
              PAINEL <span className="text-white/50">EDALIX</span>
            </span>
          </div>
          <div className="flex items-center gap-2 sm:gap-3">
            <Link
              to="/"
              data-testid="admin-view-site-link"
              className="hidden items-center gap-2 rounded-full border border-white/15 bg-white/5 px-4 py-2 text-xs font-medium text-white/75 transition-colors duration-300 hover:bg-white/10 sm:inline-flex"
            >
              <Globe className="h-3.5 w-3.5" />
              Ver site
            </Link>
            <button
              type="button"
              onClick={openPasswordModal}
              data-testid="admin-password-button"
              className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/5 px-4 py-2 text-xs font-medium text-white/75 transition-colors duration-300 hover:bg-white/10"
            >
              <KeyRound className="h-3.5 w-3.5" />
              Senha
            </button>
            <button
              type="button"
              onClick={logout}
              data-testid="admin-logout-button"
              className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/5 px-4 py-2 text-xs font-medium text-white/75 transition-colors duration-300 hover:bg-white/10"
            >
              <LogOut className="h-3.5 w-3.5" />
              Sair
            </button>
          </div>
        </div>
      </div>

      <div className="mx-auto max-w-6xl px-6 py-10">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div>
            <h1 className="font-display text-2xl font-bold tracking-tight sm:text-3xl">Projetos</h1>
            <p className="mt-1 text-sm font-light text-white/55">
              Gerencie o portfólio exibido no site. As alterações aparecem automaticamente na página pública.
            </p>
          </div>
          <button
            type="button"
            onClick={openNew}
            data-testid="admin-new-project-button"
            className="inline-flex items-center gap-2 rounded-full bg-gradient-to-r from-[#00E5FF] to-[#8A2BE2] px-6 py-3 text-sm font-semibold text-white transition-[transform,filter] duration-300 hover:scale-[1.02] hover:brightness-110"
          >
            <Plus className="h-4 w-4" />
            Novo projeto
          </button>
        </div>

        <div className="mt-8 space-y-4">
          {projects === null && (
            <div className="flex justify-center py-16">
              <Loader2 className="h-7 w-7 animate-spin text-[#00E5FF]" />
            </div>
          )}
          {projects?.length === 0 && (
            <p className="rounded-2xl border border-white/10 bg-white/[0.02] p-10 text-center text-sm text-white/50">
              Nenhum projeto cadastrado. Clique em "Novo projeto" para começar.
            </p>
          )}
          {projects?.map((p) => (
            <div
              key={p.id}
              data-testid={`admin-project-row-${p.slug}`}
              className="flex flex-col gap-4 rounded-2xl border border-white/10 bg-white/[0.02] p-4 sm:flex-row sm:items-center"
            >
              <div className="h-20 w-full shrink-0 overflow-hidden rounded-xl border border-white/10 bg-[#07070E] sm:w-32">
                {p.imagem ? (
                  <img src={imgSrc(p.imagem)} alt={`Imagem do projeto ${p.nome}`} className="h-full w-full object-cover object-top" />
                ) : (
                  <div className="flex h-full items-center justify-center">
                    <ImageIcon className="h-5 w-5 text-white/20" />
                  </div>
                )}
              </div>
              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-center gap-2">
                  <h3 className="font-display text-lg font-bold">{p.nome}</h3>
                  <span
                    className={`rounded-full px-2.5 py-1 text-[10px] font-semibold tracking-wider ${
                      p.ativo ? "bg-[#00E5FF]/10 text-[#00E5FF]" : "bg-white/5 text-white/40"
                    }`}
                    data-testid={`admin-status-${p.slug}`}
                  >
                    {p.ativo ? "ATIVO" : "INATIVO"}
                  </span>
                </div>
                <p className="mt-0.5 text-xs text-white/45">{p.categoria}</p>
                <p className="mt-1 truncate text-xs font-light text-white/55">{p.descricao}</p>
              </div>
              <div className="flex shrink-0 items-center gap-2">
                <button
                  type="button"
                  onClick={() => toggleAtivo(p)}
                  data-testid={`admin-toggle-${p.slug}`}
                  aria-label={p.ativo ? `Desativar ${p.nome}` : `Ativar ${p.nome}`}
                  className="flex h-10 w-10 items-center justify-center rounded-full border border-white/15 bg-white/5 text-white/70 transition-colors duration-300 hover:bg-white/10"
                >
                  {p.ativo ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
                <button
                  type="button"
                  onClick={() => openEdit(p)}
                  data-testid={`admin-edit-${p.slug}`}
                  aria-label={`Editar ${p.nome}`}
                  className="flex h-10 w-10 items-center justify-center rounded-full border border-white/15 bg-white/5 text-white/70 transition-colors duration-300 hover:bg-white/10"
                >
                  <Pencil className="h-4 w-4" />
                </button>
                <button
                  type="button"
                  onClick={() => remove(p)}
                  data-testid={`admin-delete-${p.slug}`}
                  aria-label={`Excluir ${p.nome}`}
                  className={`flex h-10 items-center justify-center gap-1.5 rounded-full border px-3 text-xs font-semibold transition-colors duration-300 ${
                    confirmDelete === p.id
                      ? "border-red-500/60 bg-red-500/15 text-red-300"
                      : "border-white/15 bg-white/5 text-white/70 hover:bg-white/10"
                  }`}
                >
                  <Trash2 className="h-4 w-4" />
                  {confirmDelete === p.id ? "Confirmar?" : ""}
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>

      {pwOpen && (
        <div
          className="fixed inset-0 z-[80] flex items-center justify-center bg-[#030305]/90 p-4 backdrop-blur-xl"
          data-testid="admin-password-modal"
        >
          <form onSubmit={changePassword} className="gradient-border w-full max-w-md rounded-3xl p-7 sm:p-8">
            <div className="flex items-start justify-between gap-4">
              <div>
                <h2 className="font-display text-xl font-bold tracking-tight">Alterar senha</h2>
                <p className="mt-1 text-xs font-light text-white/50">
                  A nova senha passa a valer imediatamente no próximo login.
                </p>
              </div>
              <button
                type="button"
                onClick={() => setPwOpen(false)}
                data-testid="admin-password-close-button"
                aria-label="Fechar"
                className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full border border-white/15 bg-white/5 text-white/70 hover:bg-white/10"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
            <div className="mt-6 space-y-4">
              <div>
                <span className={labelClass}>SENHA ATUAL</span>
                <input
                  type="password"
                  required
                  autoComplete="current-password"
                  value={pwForm.current}
                  onChange={(e) => setPwForm((f) => ({ ...f, current: e.target.value }))}
                  data-testid="admin-password-current-input"
                  className={inputClass}
                  placeholder="••••••••"
                />
              </div>
              <div>
                <span className={labelClass}>NOVA SENHA</span>
                <input
                  type="password"
                  required
                  minLength={8}
                  autoComplete="new-password"
                  value={pwForm.next}
                  onChange={(e) => setPwForm((f) => ({ ...f, next: e.target.value }))}
                  data-testid="admin-password-new-input"
                  className={inputClass}
                  placeholder="Mínimo de 8 caracteres"
                />
              </div>
              <div>
                <span className={labelClass}>CONFIRMAR NOVA SENHA</span>
                <input
                  type="password"
                  required
                  minLength={8}
                  autoComplete="new-password"
                  value={pwForm.confirm}
                  onChange={(e) => setPwForm((f) => ({ ...f, confirm: e.target.value }))}
                  data-testid="admin-password-confirm-input"
                  className={inputClass}
                  placeholder="Repita a nova senha"
                />
              </div>
              {pwError && (
                <p data-testid="admin-password-error" className="rounded-xl border border-red-500/30 bg-red-500/10 px-4 py-3 text-sm text-red-300">
                  {pwError}
                </p>
              )}
            </div>
            <button
              type="submit"
              disabled={pwSaving}
              data-testid="admin-password-save-button"
              className="mt-7 inline-flex w-full items-center justify-center gap-2 rounded-full bg-gradient-to-r from-[#00E5FF] to-[#8A2BE2] px-7 py-3.5 text-sm font-semibold text-white transition-[filter,opacity] duration-300 hover:brightness-110 disabled:opacity-60"
            >
              {pwSaving && <Loader2 className="h-4 w-4 animate-spin" />}
              {pwSaving ? "Salvando..." : "Salvar nova senha"}
            </button>
          </form>
        </div>
      )}

      {form && (
        <div
          className="fixed inset-0 z-[70] flex items-center justify-center bg-[#030305]/90 p-4 backdrop-blur-xl"
          data-testid="admin-form-modal"
        >
          <div className="gradient-border max-h-[92vh] w-full max-w-2xl overflow-y-auto rounded-3xl">
            <form onSubmit={save} className="p-6 sm:p-8">
              <div className="flex items-start justify-between gap-4">
                <h2 className="font-display text-xl font-bold tracking-tight sm:text-2xl">
                  {editingId === "new" ? "Novo projeto" : "Editar projeto"}
                </h2>
                <button
                  type="button"
                  onClick={() => setEditingId(null)}
                  data-testid="admin-form-close-button"
                  aria-label="Fechar formulário"
                  className="flex h-9 w-9 items-center justify-center rounded-full border border-white/15 bg-white/5 text-white/70 hover:bg-white/10"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>

              <div className="mt-6 grid gap-5 sm:grid-cols-2">
                <div>
                  <span className={labelClass}>NOME *</span>
                  <input required value={form.nome} onChange={set("nome")} data-testid="admin-form-nome" className={inputClass} placeholder="Nome do projeto" />
                </div>
                <div>
                  <span className={labelClass}>CATEGORIA</span>
                  <input value={form.categoria} onChange={set("categoria")} data-testid="admin-form-categoria" className={inputClass} placeholder="Ex.: Site para hospedagem" />
                </div>
                <div>
                  <span className={labelClass}>ENDEREÇO (SLUG)</span>
                  <input value={form.slug} onChange={set("slug")} data-testid="admin-form-slug" className={inputClass} placeholder="Gerado automaticamente" />
                  <p className="mt-1.5 text-[11px] text-white/35">Define a página /projetos/slug. Deixe vazio para gerar do nome.</p>
                </div>
                <div>
                  <span className={labelClass}>URL DO PROJETO</span>
                  <input value={form.url} onChange={set("url")} data-testid="admin-form-url" className={inputClass} placeholder="https://meuprojeto.com.br" />
                  <p className="mt-1.5 text-[11px] text-white/35">Use # enquanto o projeto não estiver publicado.</p>
                </div>
                <div className="sm:col-span-2">
                  <span className={labelClass}>DESCRIÇÃO CURTA</span>
                  <textarea rows={2} value={form.descricao} onChange={set("descricao")} data-testid="admin-form-descricao" className={`${inputClass} resize-none`} placeholder="Texto exibido no card da página inicial" />
                </div>
                <div className="sm:col-span-2">
                  <span className={labelClass}>DESCRIÇÃO COMPLETA</span>
                  <textarea rows={3} value={form.descricaoCompleta} onChange={set("descricaoCompleta")} data-testid="admin-form-descricao-completa" className={`${inputClass} resize-none`} placeholder="Texto exibido na página do projeto" />
                </div>
                <div className="sm:col-span-2">
                  <ImagePicker label="IMAGEM PRINCIPAL" value={form.imagem} onChange={set("imagem")} testid="admin-form-imagem" />
                </div>
                <div className="sm:col-span-2">
                  <span className={labelClass}>GALERIA</span>
                  <div className="flex flex-wrap items-center gap-3">
                    {form.galeria.map((img, i) => (
                      <div key={i} className="relative h-16 w-24 overflow-hidden rounded-lg border border-white/10">
                        <img src={imgSrc(img)} alt={`Galeria ${i + 1}`} className="h-full w-full object-cover object-top" />
                        <button
                          type="button"
                          onClick={() => setForm((f) => ({ ...f, galeria: f.galeria.filter((_, x) => x !== i) }))}
                          data-testid={`admin-form-gallery-remove-${i}`}
                          aria-label={`Remover imagem ${i + 1} da galeria`}
                          className="absolute right-1 top-1 flex h-5 w-5 items-center justify-center rounded-full bg-black/70 text-white/80 hover:text-white"
                        >
                          <X className="h-3 w-3" />
                        </button>
                      </div>
                    ))}
                    <button
                      type="button"
                      onClick={() => galleryInputRef.current?.click()}
                      disabled={galleryUploading}
                      data-testid="admin-form-gallery-add-button"
                      className="flex h-16 w-24 items-center justify-center rounded-lg border border-dashed border-white/20 text-white/40 transition-colors duration-300 hover:border-[#00E5FF]/50 hover:text-[#00E5FF] disabled:opacity-60"
                    >
                      {galleryUploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}
                    </button>
                    <input ref={galleryInputRef} type="file" accept="image/png,image/jpeg,image/webp" className="hidden" onChange={addGalleryImage} />
                  </div>
                </div>
                <div>
                  <span className={labelClass}>TECNOLOGIAS</span>
                  <input value={form.tecnologias} onChange={set("tecnologias")} data-testid="admin-form-tecnologias" className={inputClass} placeholder="React, Node, MongoDB" />
                  <p className="mt-1.5 text-[11px] text-white/35">Separe por vírgulas.</p>
                </div>
                <div>
                  <span className={labelClass}>ORDEM</span>
                  <input type="number" min="0" value={form.ordem} onChange={set("ordem")} data-testid="admin-form-ordem" className={inputClass} />
                </div>
                <div className="sm:col-span-2">
                  <span className={labelClass}>FUNCIONALIDADES</span>
                  <textarea rows={3} value={form.funcionalidades} onChange={set("funcionalidades")} data-testid="admin-form-funcionalidades" className={`${inputClass} resize-none`} placeholder={"Uma por linha.\nEx.: Cardápio digital com categorias"} />
                </div>
                <div className="sm:col-span-2">
                  <span className={labelClass}>OBJETIVO</span>
                  <textarea rows={2} value={form.objetivo} onChange={set("objetivo")} data-testid="admin-form-objetivo" className={`${inputClass} resize-none`} placeholder="Objetivo do projeto" />
                </div>
                <label className="flex items-center gap-3 sm:col-span-2" data-testid="admin-form-ativo-label">
                  <input
                    type="checkbox"
                    checked={form.ativo}
                    onChange={set("ativo")}
                    data-testid="admin-form-ativo"
                    className="h-4 w-4 accent-[#00E5FF]"
                  />
                  <span className="text-sm text-white/70">Projeto ativo (visível no site público)</span>
                </label>
              </div>

              <button
                type="submit"
                disabled={saving}
                data-testid="admin-form-save-button"
                className="mt-8 inline-flex w-full items-center justify-center gap-2 rounded-full bg-gradient-to-r from-[#00E5FF] to-[#8A2BE2] px-7 py-3.5 text-sm font-semibold text-white transition-[transform,filter,opacity] duration-300 hover:brightness-110 disabled:opacity-60"
              >
                {saving && <Loader2 className="h-4 w-4 animate-spin" />}
                {saving ? "Salvando..." : "Salvar projeto"}
              </button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Admin;
